// Painted markings on the turf: the start line, lane numbers, the chequered finish stripe,
// and distance poles every 100 m so viewers can read progress along the straight.
import {
  CylinderGeometry, Group, InstancedMesh, Matrix4, Mesh, MeshStandardMaterial, NearestFilter,
  PlaneGeometry, Quaternion, Vector3,
} from 'three';
import { LANE_COUNT, LANE_WIDTH, TRACK_LENGTH, laneCenterZ } from '../../data/track';
import { TRACK_HALF_WIDTH } from './layout';
import { PALETTE, SIGN_FONT, canvasTexture, rng } from './util';

function paint(map: ReturnType<typeof canvasTexture>, offset = -4): MeshStandardMaterial {
  const m = new MeshStandardMaterial({ map, transparent: true, roughness: 0.85, depthWrite: false });
  m.polygonOffset = true; m.polygonOffsetFactor = offset; m.polygonOffsetUnits = offset * 2;
  return m;
}

/** Scuffed white paint: a fill with grass showing through in small gaps. */
function scuffed(g: CanvasRenderingContext2D, w: number, h: number, seed: number): void {
  const r = rng(seed);
  g.globalCompositeOperation = 'destination-out';
  for (let i = 0; i < (w * h) / 60; i++) {
    g.fillStyle = `rgba(0,0,0,${0.25 + r() * 0.6})`;
    g.fillRect(r() * w, r() * h, 1 + r() * 3, 1 + r() * 2);
  }
  g.globalCompositeOperation = 'source-over';
}

export class TrackMarkings {
  readonly group = new Group();

  constructor() {
    this.group.name = 'track-markings';
    const width = LANE_COUNT * LANE_WIDTH;

    // Finish stripe: 1 m of chequers across every lane, centred on the finish line.
    const checker = canvasTexture(64, 928, (g, w, h) => {
      const s = w / 2;
      for (let y = 0; y < h / s; y++) for (let x = 0; x < 2; x++) {
        g.fillStyle = (x + y) % 2 ? '#141012' : '#f6f1e6';
        g.fillRect(x * s, y * s, s, s);
      }
      scuffed(g, w, h, 3);
    });
    checker.magFilter = NearestFilter;
    const finish = new Mesh(new PlaneGeometry(1, width).rotateX(-Math.PI / 2), paint(checker));
    finish.position.set(TRACK_LENGTH, 0.01, 0);
    finish.receiveShadow = true;
    finish.name = 'finish-stripe';
    this.group.add(finish);

    // Start line.
    const chalk = canvasTexture(16, 512, (g, w, h) => { g.fillStyle = '#f7f3ea'; g.fillRect(0, 0, w, h); scuffed(g, w, h, 5); });
    const start = new Mesh(new PlaneGeometry(0.14, width).rotateX(-Math.PI / 2), paint(chalk));
    start.position.set(0.12, 0.01, 0);
    start.receiveShadow = true;
    this.group.add(start);

    // Lane numbers painted just after the start line, readable from the stalls.
    const numbers = canvasTexture(256 * LANE_COUNT, 256, (g, _w, h) => {
      g.textAlign = 'center'; g.textBaseline = 'middle';
      g.font = `900 200px ${SIGN_FONT}`;
      for (let i = 0; i < LANE_COUNT; i++) { g.fillStyle = '#f7f3ea'; g.fillText(String(i + 1), i * 256 + 128, h / 2 + 8); }
      scuffed(g, 256 * LANE_COUNT, h, 9);
    });
    for (let i = 0; i < LANE_COUNT; i++) {
      const geo = new PlaneGeometry(1.3, 1.3);
      const uv = geo.getAttribute('uv');
      for (let k = 0; k < uv.count; k++) uv.setX(k, (i + uv.getX(k)) / LANE_COUNT);
      geo.rotateX(-Math.PI / 2).rotateY(-Math.PI / 2);
      const m = new Mesh(geo, paint(numbers));
      m.position.set(3.2, 0.01, laneCenterZ(i));
      m.receiveShadow = true;
      this.group.add(m);
    }

    // Distance poles: white posts with a plum plate every 100 m, on both outer edges.
    const plates = canvasTexture(256 * 6, 128, (g, _w, h) => {
      for (let i = 0; i < 6; i++) {
        g.fillStyle = PALETTE.plum; g.fillRect(i * 256, 0, 256, h);
        g.strokeStyle = PALETTE.gold; g.lineWidth = 8; g.strokeRect(i * 256 + 6, 6, 244, h - 12);
        g.fillStyle = PALETTE.ivory; g.font = `900 72px ${SIGN_FONT}`; g.textAlign = 'center'; g.textBaseline = 'middle';
        g.fillText(`${(i + 1) * 100}`, i * 256 + 128, h / 2 + 4);
      }
    });
    const poleMat = new MeshStandardMaterial({ color: '#f3eee4', roughness: 0.6 });
    const plateMat = new MeshStandardMaterial({ map: plates, roughness: 0.6 });
    const poles = new InstancedMesh(new CylinderGeometry(0.05, 0.06, 1.6, 10).translate(0, 0.8, 0), poleMat, 12);
    poles.castShadow = true;
    let n = 0;
    for (let i = 1; i <= 6; i++) {
      for (const side of [-1, 1]) {
        const z = side * (TRACK_HALF_WIDTH + 0.55);
        poles.setMatrixAt(n++, new Matrix4().compose(new Vector3(i * 100, 0, z), new Quaternion(), new Vector3(1, 1, 1)));
        const geo = new PlaneGeometry(0.62, 0.31);
        const uv = geo.getAttribute('uv');
        for (let k = 0; k < uv.count; k++) uv.setX(k, (i - 1 + uv.getX(k)) / 6);
        // Plates face back down the track so the approaching field (and the chase camera) reads them.
        geo.rotateY(-Math.PI / 2);
        const plate = new Mesh(geo, plateMat);
        plate.position.set(i * 100 - 0.07, 1.45, z);
        plate.castShadow = true;
        this.group.add(plate);
        const back = plate.clone(); back.rotation.y = Math.PI; back.position.x = i * 100 + 0.07; this.group.add(back);
      }
    }
    poles.instanceMatrix.needsUpdate = true;
    this.group.add(poles);
  }
}
